import { useState, useEffect } from "react";
import { langPath, switchLangUrl, saveLangPref } from "./utils/lang";
import { setPageMeta } from "./utils/meta";

// 言語別PDFリンク。ファイルが用意できた時点で /public に配置してください。
const PDF_LINKS = {
  th: '/nunt-document-th.pdf',
  en: '/nunt-document-en.pdf',
  jp: '/nunt-document-jp.pdf',
};

export default function Download({ language = 'th' }) {
  const [formData, setFormData] = useState({
    company: "", name: "", email: "", phone: "", privacy: false,
  });
  const [status, setStatus] = useState(null);

  useEffect(() => { setPageMeta(language, '/download'); }, [language]);

  const root = langPath(language, '/');
  const contact = langPath(language, '/contact');
  const privacy = langPath(language, '/privacy');
  const pdfHref = PDF_LINKS[language] || '/nunt-document.pdf';
  const isEn = language === 'en';
  const isTh = language === 'th';

  /* 3言語切替ヘルパー */
  const t = (th, en, jp) => isTh ? th : isEn ? en : jp;

  const tr = {
    backLink: t("← กลับไปหน้าหลัก", "← Back to Top", "← トップに戻る"),
    badge: t("ใช้เวลาประมาณ 1 นาที", "Takes about 1 minute", "1分で完了"),
    pageTitle: t("ดาวน์โหลดเอกสารบริการ Nunt", "Download Nunt Service Materials", "Nuntサービス資料ダウンロード"),
    subcopy: t("เราจะส่งเอกสารที่อธิบายวิธีใช้ Nunt และภาพรวมของราคาให้คุณ", "We will send you materials that explain how Nunt can be used and provide an overview of pricing.", "タレント素材の活用方法や料金イメージをまとめた資料をお送りします。"),
    requiredNote: t("* เป็นข้อมูลที่จำเป็น", "* Required fields", "* は必須項目です"),
    labelCompany: t("ชื่อบริษัท", "Company Name", "会社名"),
    labelName: t("ชื่อผู้ติดต่อ", "Contact Person", "担当者名"),
    labelEmail: t("อีเมล", "Email Address", "メールアドレス"),
    labelPhone: t("เบอร์โทรศัพท์", "Phone Number", "電話番号"),
    phCompany: t("ตัวอย่าง: ABC Clinic Co., Ltd.", "Example: ABC Clinic Co., Ltd.", "例：ABC Clinic Co., Ltd."),
    phName: t("ตัวอย่าง: Tanaka", "Example: Tanaka", "例：Tanaka"),
    phEmail: "example@company.com",
    phPhone: "+66 80 000 0000",
    privacyConsent: isTh
      ? "ข้อมูลส่วนบุคคลที่กรอกจะได้รับการจัดการอย่างเหมาะสมตาม "
      : isEn
      ? "Your personal information will be handled appropriately in accordance with our "
      : "ご入力いただいた個人情報は、",
    privacyConsentAfter: isTh
      ? " ฉันยอมรับและต้องการส่งแบบฟอร์มนี้"
      : isEn
      ? ". I agree and would like to submit this form."
      : "の記載に基づいて適切に管理します。上記に同意して送信する",
    privacyAlert: t("กรุณายอมรับนโยบายความเป็นส่วนตัว", "Please agree to the Privacy Policy.", "プライバシーポリシーに同意してください。"),
    submitBtn: t("ขอรับเอกสาร", "Request Materials", "上記に同意して資料を請求する"),
    submitting: t("กำลังส่ง...", "Sending...", "送信中..."),
    errorMsg: t("ส่งคำขอไม่สำเร็จ กรุณาลองใหม่อีกครั้งภายหลัง", "Failed to send your request. Please try again later.", "送信に失敗しました。時間をおいて再度お試しください。"),
    successTitle: t("ขอบคุณค่ะ", "Thank you!", "ありがとうございます！"),
    successBody1: t("เราได้รับคำขอของคุณแล้ว", "Your request has been received.", "お申し込みを受け付けました。"),
    successBody2: t("กรุณากดปุ่มด้านล่างเพื่อดาวน์โหลดเอกสาร", "Please use the button below to download the materials.", "下のボタンから資料をダウンロードしてください。"),
    downloadBtn: t("⬇ ดาวน์โหลดเอกสาร", "⬇ Download Materials", "⬇ 資料をダウンロードする"),
    successSupplement: t("หากมีคำถามเกี่ยวกับเอกสาร สามารถติดต่อเราได้ทุกเมื่อ", "If you have any questions about the materials, please feel free to contact us.", "資料についてご不明な点がございましたら、\nお気軽にお問い合わせください。"),
    contactCta: t("ปรึกษาฟรี →", "Contact Us →", "無料相談する →"),
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.privacy) {
      alert(tr.privacyAlert);
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch("https://formspree.io/f/mojpbped", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          _subject: "【Nunt】資料ダウンロードのお申し込み",
          form_type: "download",
          language: language,
          会社名: formData.company,
          担当者名: formData.name,
          メールアドレス: formData.email,
          電話番号: formData.phone || "未記入",
        }),
      });
      if (res.ok) {
        setStatus("success");
        setFormData({ company: "", name: "", email: "", phone: "", privacy: false });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const inputStyle = {
    width: "100%", padding: "13px 14px",
    border: "1px solid rgba(6, 24, 69, 0.14)", borderRadius: 8,
    fontSize: 14, color: "#001A3D", outline: "none",
    background: "#fff",
    fontFamily: "'Noto Sans JP', sans-serif",
    transition: "border-color 0.18s ease, box-shadow 0.18s ease",
  };

  const labelStyle = {
    fontSize: 13, fontWeight: 800,
    color: "#061845", marginBottom: 8, display: "block",
  };

  return (
    <div className="download-page" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#001A3D", background: "#fff", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .download-page {
          background:
            radial-gradient(circle at 80% 8%, rgba(116, 105, 216, 0.12) 0, rgba(116, 105, 216, 0) 32%),
            linear-gradient(180deg, #fff 0%, #fbfaff 100%) !important;
        }
        .download-page input::placeholder {
          color: #939bb1;
        }
        .download-page input:focus {
          border-color: #d8a132 !important;
          box-shadow: 0 0 0 3px rgba(216, 161, 50, 0.14);
        }
        .download-back-link,
        .download-privacy-link,
        .download-secondary-link,
        .download-submit-button,
        .download-file-button {
          transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
        }
        .download-back-link:hover,
        .download-privacy-link:hover,
        .download-secondary-link:hover {
          color: #b88418 !important;
          border-color: rgba(184, 132, 24, 0.55) !important;
        }
        .download-submit-button:not(:disabled):hover,
        .download-file-button:hover {
          background: linear-gradient(135deg, #b96e07 0%, #d79a24 48%, #a96306 100%) !important;
          box-shadow: 0 10px 22px rgba(184, 132, 24, 0.18) !important;
          transform: translateY(-1px);
        }
        .download-lang-btn {
          font-size: 11px; font-weight: 600;
          color: #bbb; text-decoration: none; padding: 4px 4px;
          line-height: 1; transition: color 0.15s ease;
        }
        .download-lang-btn:hover { color: #061845; }
        .download-lang-btn.is-active {
          font-weight: 900; color: #061845;
          text-decoration: underline; text-underline-offset: 2px;
        }
        @media (max-width: 768px) {
          .download-header {
            padding: 12px 18px !important;
          }
          .download-logo {
            height: 66px !important;
          }
          .download-main {
            padding: 42px 18px 52px !important;
          }
          .download-title-area {
            margin-bottom: 28px !important;
          }
          .download-title {
            font-size: 25px !important;
          }
          .download-form-card {
            padding: 22px !important;
          }
          .download-success-card {
            padding: 32px 22px !important;
          }
        }
      `}</style>

      {/* ヘッダー */}
      <header className="download-header" style={{
        background: "rgba(255, 255, 255, 0.95)", borderBottom: "1px solid rgba(16, 30, 75, 0.08)",
        padding: "16px 24px", display: "flex", alignItems: "center",
        boxShadow: "0 10px 28px rgba(33, 26, 82, 0.04)",
      }}>
        <a href={root} style={{ textDecoration: "none" }}>
          <img className="download-logo" src="/nuntlogo.png" alt="Nunt" style={{ height: 90, width: "auto" }} />
        </a>

        {/* 言語切替 */}
        <div style={{ display: "flex", alignItems: "center", gap: 3, marginLeft: "auto", marginRight: 14 }}>
          {[['th','TH'],['en','EN'],['jp','JP']].map(([l, label], i) => (
            <span key={l} style={{ display: "flex", alignItems: "center" }}>
              {i > 0 && <span style={{ color: "#d0d0d0", fontSize: 10, margin: "0 2px" }}>/</span>}
              <a
                className={`download-lang-btn${language === l ? " is-active" : ""}`}
                href={switchLangUrl(l)}
                onClick={() => saveLangPref(l)}
              >{label}</a>
            </span>
          ))}
        </div>

        <a className="download-back-link" href={root} style={{
          fontSize: 13, color: "#061845",
          textDecoration: "none", fontWeight: 700,
          display: "flex", alignItems: "center", gap: 4,
        }}>
          {tr.backLink}
        </a>
      </header>

      {/* コンテンツ */}
      <main className="download-main" style={{ maxWidth: 600, margin: "0 auto", padding: "64px 24px 72px" }}>

        {/* タイトルエリア */}
        <div className="download-title-area" style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            minHeight: 28,
            background: "#fff8ed", color: "#b88418",
            border: "1px solid rgba(184, 132, 24, 0.22)",
            borderRadius: 999, padding: "0 14px",
            fontSize: 12, fontWeight: 800, marginBottom: 16,
          }}>
            {tr.badge}
          </div>
          <h1 className="download-title" style={{ fontSize: 28, fontWeight: 900, color: "#061845", lineHeight: 1.45 }}>
            {tr.pageTitle}
          </h1>
          <p style={{ marginTop: 14, fontSize: 13.5, color: "#4b587b", lineHeight: 1.8 }}>
            {tr.subcopy}
          </p>
        </div>

        {/* フォーム */}
        {status === "success" && (
          <div className="download-success-card" style={{
            background: "rgba(255, 255, 255, 0.95)",
            border: "1px solid rgba(116, 105, 216, 0.18)",
            borderRadius: 8, padding: "40px 24px", textAlign: "center",
            boxShadow: "0 14px 34px rgba(32, 28, 84, 0.06)",
          }}>
            <div style={{
              width: 48, height: 48, margin: "0 auto 16px",
              borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "#f6f4ff", color: "#061845",
              border: "1px solid rgba(116, 105, 216, 0.18)",
              fontSize: 24, fontWeight: 900,
            }}>
              ✓
            </div>
            <p style={{ fontSize: 22, fontWeight: 900, color: "#061845", marginBottom: 12 }}>
              {tr.successTitle}
            </p>
            <p style={{ fontSize: 14, color: "#444", lineHeight: 1.9, marginBottom: 32 }}>
              {tr.successBody1}<br />
              {tr.successBody2}
            </p>
            <a
              className="download-file-button"
              href={pdfHref}
              download="Nuntサービス資料.pdf"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "linear-gradient(135deg, #c77b0a 0%, #e0a127 48%, #bf7107 100%)", color: "#fff",
                borderRadius: 8, padding: "16px 32px",
                fontSize: 15, fontWeight: 700,
                textDecoration: "none", marginBottom: 24,
                boxShadow: "0 8px 18px rgba(192, 113, 7, 0.14)",
              }}
            >
              {tr.downloadBtn}
            </a>
            <div style={{ borderTop: "1px solid rgba(6, 24, 69, 0.1)", paddingTop: 24, marginTop: 8 }}>
              <p style={{ fontSize: 13, color: "#555", marginBottom: 16, whiteSpace: "pre-line" }}>
                {tr.successSupplement}
              </p>
              <a
                className="download-secondary-link"
                href={contact}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: "#fff", color: "#061845",
                  border: "1.5px solid rgba(6, 24, 69, 0.62)",
                  borderRadius: 8, padding: "10px 24px",
                  fontSize: 13, fontWeight: 700,
                  textDecoration: "none",
                }}
              >
                {tr.contactCta}
              </a>
            </div>
          </div>
        )}
        {status !== "success" && (
          <form className="download-form-card" onSubmit={handleSubmit} style={{
            background: "rgba(255, 255, 255, 0.95)",
            border: "1px solid rgba(6, 24, 69, 0.1)",
            borderRadius: 8,
            padding: "36px 32px",
            boxShadow: "0 14px 34px rgba(32, 28, 84, 0.06)",
          }}>
            <p style={{ fontSize: 12, color: "#888", marginBottom: 24, textAlign: "right" }}>
              <span style={{ color: "#e02020" }}>*</span> {isTh ? tr.requiredNote.replace('* ', '') : isEn ? "Required fields" : "は必須項目です"}
            </p>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>
                {tr.labelCompany} <span style={{ color: "#e02020" }}>*</span>
              </label>
              <input
                style={inputStyle} type="text" name="company"
                value={formData.company} onChange={handleChange}
                placeholder={tr.phCompany} required
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>
                {tr.labelName} <span style={{ color: "#e02020" }}>*</span>
              </label>
              <input
                style={inputStyle} type="text" name="name"
                value={formData.name} onChange={handleChange}
                placeholder={tr.phName} required
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>
                {tr.labelEmail} <span style={{ color: "#e02020" }}>*</span>
              </label>
              <input
                style={inputStyle} type="email" name="email"
                value={formData.email} onChange={handleChange}
                placeholder={tr.phEmail} required
              />
            </div>

            <div style={{ marginBottom: 28 }}>
              <label style={labelStyle}>
                {tr.labelPhone} <span style={{ color: "#888", fontWeight: 400 }}>{isTh ? `(${tr.optional})` : isEn ? "(Optional)" : "（任意）"}</span>
              </label>
              <input
                style={inputStyle} type="tel" name="phone"
                value={formData.phone} onChange={handleChange}
                placeholder={tr.phPhone}
              />
            </div>

            <div style={{ marginBottom: 28, display: "flex", alignItems: "flex-start", gap: 10 }}>
              <input
                type="checkbox" name="privacy" id="privacy"
                checked={formData.privacy} onChange={handleChange}
                style={{ marginTop: 3, width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
              />
              <label htmlFor="privacy" style={{ fontSize: 12, color: "#444", cursor: "pointer", lineHeight: 1.7 }}>
                {isTh ? (
                  <>
                    ข้อมูลส่วนบุคคลที่กรอกจะได้รับการจัดการอย่างเหมาะสมตาม{" "}
                    <a className="download-privacy-link" href={privacy} style={{ color: "#061845", textDecoration: "underline", textUnderlineOffset: 3 }}>นโยบายความเป็นส่วนตัว</a>
                    {" "}ฉันยอมรับและต้องการส่งแบบฟอร์มนี้
                  </>
                ) : isEn ? (
                  <>
                    Your personal information will be handled appropriately in accordance with our{" "}
                    <a className="download-privacy-link" href={privacy} style={{ color: "#061845", textDecoration: "underline", textUnderlineOffset: 3 }}>Privacy Policy</a>.
                    {" "}I agree and would like to submit this form.
                  </>
                ) : (
                  <>
                    ご入力いただいた個人情報は、
                    <a className="download-privacy-link" href={privacy} style={{ color: "#061845", textDecoration: "underline", textUnderlineOffset: 3 }}>プライバシーポリシー</a>
                    の記載に基づいて適切に管理します。上記に同意して送信する
                  </>
                )}
              </label>
            </div>

            <button
              className="download-submit-button"
              type="submit"
              disabled={status === "sending"}
              style={{
                width: "100%", padding: "16px",
                background: status === "sending" ? "#cfd3dc" : "linear-gradient(135deg, #c77b0a 0%, #e0a127 48%, #bf7107 100%)",
                color: "#fff", border: "none", borderRadius: 8,
                boxShadow: status === "sending" ? "none" : "0 8px 18px rgba(192, 113, 7, 0.14)",
                fontSize: 15, fontWeight: 700,
                cursor: status === "sending" ? "not-allowed" : "pointer",
              }}
            >
              {status === "sending" ? tr.submitting : tr.submitBtn}
            </button>

            {status === "error" && (
              <p style={{ color: "#e02020", fontSize: 13, marginTop: 12, textAlign: "center" }}>
                {tr.errorMsg}
              </p>
            )}
          </form>
        )}
      </main>

      {/* フッター */}
      <footer style={{ background: "#fff", borderTop: "1px solid rgba(16, 30, 75, 0.08)", padding: "24px", textAlign: "center" }}>
        <p style={{ fontSize: 12, color: "#888" }}>© 2026 Patlush All Rights Reserved.</p>
      </footer>
    </div>
  );
}
